'use client';

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Container } from '@mantine/core';
import AOS from 'aos';
import 'aos/dist/aos.css';

import styles from './Itineraire.module.scss';

type ImageFormat = 'portrait' | 'landscape' | 'square';

export interface ItineraireSectionImage {
    url: string;
    alt: string;
    format?: ImageFormat;
}

export interface ItineraireSection {
    title: string;
    paragraphs: string[];
    images?: ItineraireSectionImage[];
}

export interface ItineraireSlice {
    title?: string;
    sections: ItineraireSection[];
}

interface Props {
    slice: ItineraireSlice;
}

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

function useElementHeight(ref: React.RefObject<HTMLElement | null>) {
    const [h, setH] = useState<number>(0);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const update = () => {
            const rect = el.getBoundingClientRect();
            setH(Math.round(rect.height));
        };

        const ro = new ResizeObserver(update);
        ro.observe(el);
        update();

        return () => ro.disconnect();
    }, [ref]);

    return h;
}

function useSmoothSectionMotion(
    ref: React.RefObject<HTMLElement | null>,
    onProgress?: (progress: number, el: HTMLElement) => void
) {
    const onProgressRef = useRef(onProgress);

    useEffect(() => {
        onProgressRef.current = onProgress;
    }, [onProgress]);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        let raf = 0;
        let current = 0;
        let target = 0;
        let running = false;

        const getTargetProgress = () => {
            const rect = el.getBoundingClientRect();
            const vh = window.innerHeight;

            const start = vh * 0.7;
            const end = vh * 0.25;

            const travel = rect.height + (start - end);
            const moved = start - rect.top;

            return travel > 0 ? clamp01(moved / travel) : 0;
        };

        const render = () => {
            const delta = target - current;

            current += delta * 0.14;

            if (Math.abs(target - current) < 0.001) {
                current = target;
            }

            el.style.setProperty('--p', String(current));
            onProgressRef.current?.(current, el);

            if (Math.abs(target - current) > 0.001) {
                raf = requestAnimationFrame(render);
            } else {
                running = false;
            }
        };

        const requestUpdate = () => {
            target = getTargetProgress();

            if (!running) {
                running = true;
                raf = requestAnimationFrame(render);
            }
        };

        requestUpdate();

        window.addEventListener('scroll', requestUpdate, { passive: true });
        window.addEventListener('resize', requestUpdate);

        return () => {
            cancelAnimationFrame(raf);
            window.removeEventListener('scroll', requestUpdate);
            window.removeEventListener('resize', requestUpdate);
        };
    }, [ref]);
}

export default function ItineraireImmersive({ slice }: Props) {
    useEffect(() => {
        AOS.init({
            duration: 650,
            easing: 'ease-out-cubic',
            once: true,
            offset: 120
        });
    }, []);

    if (!slice?.sections?.length) return null;

    return (
        <section className={styles.section}>
            <Container size="lg">
                {slice.title ? (
                    <h1 className={styles.h1} data-aos="fade-up">
                        {slice.title}
                    </h1>
                ) : null}

                <div className={styles.sections}>
                    {slice.sections.map((sec, idx) => (
                        <SectionItem key={`${sec.title}-${idx}`} sec={sec} idx={idx} />
                    ))}
                </div>
            </Container>
        </section>
    );
}

function SectionItem({ sec, idx }: { sec: ItineraireSection; idx: number }) {
    const reverse = idx % 2 === 1;

    const textRef = useRef<HTMLDivElement | null>(null);
    const paragraphsRef = useRef<(HTMLParagraphElement | null)[]>([]);

    const textHeight = useElementHeight(textRef);

    const imageCount = useMemo(() => {
        if (textHeight < 240) return 2;
        if (textHeight < 360) return 3;
        if (textHeight < 520) return 4;
        return 5;
    }, [textHeight]);

    const images = (sec.images || []).slice(0, imageCount);

    const collageClass = useMemo(() => {
        const n = Math.min(Math.max(images.length, 2), 5);
        return styles[`collage_${n}`];
    }, [images.length]);

    const [revealed, setRevealed] = useState(false);
    const [activeParagraphIndex, setActiveParagraphIndex] = useState(-1);

    useEffect(() => {
        const el = textRef.current;
        if (!el) return;

        const io = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setRevealed(true);
                    io.disconnect();
                }
            },
            { threshold: 0.25 }
        );

        io.observe(el);
        return () => io.disconnect();
    }, []);

    useSmoothSectionMotion(textRef, (progress, container) => {
        if (!revealed) return;

        const paragraphNodes = paragraphsRef.current.filter(Boolean) as HTMLParagraphElement[];
        if (!paragraphNodes.length) return;

        const dotY = progress * (container.offsetHeight - 12) + 6;

        let closestIndex = -1;
        let closestDistance = Infinity;

        paragraphNodes.forEach((p, i) => {
            const centerY = p.offsetTop + p.offsetHeight / 2;
            const distance = Math.abs(centerY - dotY);

            if (distance < closestDistance) {
                closestDistance = distance;
                closestIndex = i;
            }
        });

        setActiveParagraphIndex((prev) => (prev === closestIndex ? prev : closestIndex));
    });

    useEffect(() => {
        const t = window.setTimeout(() => {
            if (typeof window !== 'undefined') {
                AOS.refreshHard();
            }
        }, 60);

        return () => window.clearTimeout(t);
    }, [textHeight, images.length]);

    return (
        <article className={`${styles.item} ${reverse ? styles.reverse : ''}`}>
            <div className={styles.images}>
                <div
                    className={`${styles.mosaic} ${collageClass}`}
                    style={
                        textHeight
                            ? ({ height: `${textHeight}px` } as React.CSSProperties)
                            : undefined
                    }
                    data-aos={reverse ? 'fade-right' : 'fade-left'}
                    data-aos-anchor-placement="top-bottom"
                >
                    {images.map((img, j) => (
                        <figure
                            key={`${img.url}-${j}`}
                            className={styles.imgCard}
                            data-aos="zoom-in"
                            data-aos-delay={120 + j * 120}
                            data-aos-duration="650"
                            data-aos-easing="ease-out-cubic"
                            data-aos-anchor-placement="top-bottom"
                        >
                            <img
                                src={img.url}
                                alt={img.alt}
                                loading="lazy"
                                decoding="async"
                            />
                        </figure>
                    ))}
                </div>
            </div>

            <div className={styles.text}>
                <div
                    ref={textRef}
                    className={styles.textInner}
                    data-aos={reverse ? 'fade-left' : 'fade-right'}
                >
                    <div className={styles.timeline} aria-hidden="true">
                        <span className={styles.rail} />
                        <span className={styles.dot} />
                    </div>

                    <div className={styles.textContent}>
                        <h2 className={styles.h2} data-aos="fade-up" data-aos-delay="80">
                            {sec.title}
                        </h2>

                        <div
                            className={`${styles.paragraphs} ${revealed ? styles.revealed : ''}`}
                        >
                            {sec.paragraphs?.map((p, k) => (
                                <p
                                    key={k}
                                    ref={(node) => {
                                        paragraphsRef.current[k] = node;
                                    }}
                                    className={
                                        k === activeParagraphIndex ? styles.activeParagraph : ''
                                    }
                                    style={{ ['--i' as any]: k }}
                                >
                                    {p}
                                </p>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </article>
    );
}